import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import walletService from '../services/api/walletService';

const WalletCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Verifying your wallet top-up...');
  const [newBalance, setNewBalance] = useState(null);

  const verifyTopUp = async () => {
    const txRef = searchParams.get('tx_ref');
    const flwStatus = searchParams.get('status');

    if (!txRef) {
      setStatus('error');
      setMessage('Invalid payment reference');
      return;
    }

    // Check Flutterwave status
    if (flwStatus === 'cancelled') {
      setStatus('cancelled');
      setMessage('Payment was cancelled');
      setTimeout(() => navigate('/wallet'), 3000);
      return;
    }

    try {
      const response = await walletService.verifyTopUp(txRef);

      if (response.success) {
        setStatus('success');
        setMessage('Wallet topped up successfully!');
        setNewBalance(response.data.newBalance);
        setTimeout(() => navigate('/wallet'), 3000);
      } else {
        setStatus('error');
        setMessage(response.message || 'Top-up verification failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage(error.response?.data?.error || 'Failed to verify top-up');
    }
  };

  useEffect(() => {
    verifyTopUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === 'verifying' && (
          <>
            <div style={styles.spinner}></div>
            <h2 style={styles.title}>Verifying Payment</h2>
            <p style={styles.message}>{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.title}>Top-up Successful!</h2>
            <p style={styles.message}>{message}</p>
            {newBalance !== null && (
              <div style={styles.balanceCard}>
                <p style={styles.balanceLabel}>New Balance</p>
                <p style={styles.balanceAmount}>{formatAmount(newBalance)}</p>
              </div>
            )}
            <p style={styles.redirect}>Redirecting to wallet...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={styles.errorIcon}>✕</div>
            <h2 style={styles.title}>Top-up Failed</h2>
            <p style={styles.message}>{message}</p>
            <button 
              style={styles.button} 
              onClick={() => navigate('/wallet')}
            >
              Return to Wallet
            </button>
          </>
        )}

        {status === 'cancelled' && (
          <>
            <div style={styles.warningIcon}>!</div>
            <h2 style={styles.title}>Payment Cancelled</h2>
            <p style={styles.message}>{message}</p>
            <button 
              style={styles.button} 
              onClick={() => navigate('/wallet')}
            >
              Return to Wallet
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  card: {
    background: 'white',
    padding: '3rem',
    borderRadius: '16px',
    boxShadow: '0 10px 50px rgba(0, 0, 0, 0.2)',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 2rem',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#10b981',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '3rem',
    margin: '0 auto 2rem',
  },
  errorIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#ef4444',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '3rem',
    margin: '0 auto 2rem',
  },
  warningIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#f59e0b',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '3rem',
    margin: '0 auto 2rem',
  },
  title: {
    fontSize: '1.75rem',
    marginBottom: '1rem',
    color: '#333',
  },
  message: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '1.5rem',
  },
  balanceCard: {
    background: '#f8f9fa',
    padding: '1.5rem',
    borderRadius: '12px',
    margin: '1.5rem 0',
  },
  balanceLabel: {
    fontSize: '0.9rem',
    color: '#666',
    marginBottom: '0.5rem',
  },
  balanceAmount: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#667eea',
    margin: 0,
  },
  redirect: {
    fontSize: '0.9rem',
    color: '#999',
    marginTop: '1rem',
  },
  button: {
    padding: '0.875rem 2rem',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '1rem',
  },
};

export default WalletCallback;
